import {useMemo} from "react";
import InputGroup from "./InputGroup";
import SurveyResponseGrid from "./SurveyResponseGrid";
import {ageCellText, genderCellText} from "../../libs/surveyDemographics";
import {currentQuestionNumbers} from "../../libs/surveyQuestions";

/**
 * '설문조사 분석' 탭 하단 — **응답자별 응답 시트**(구글폼 응답 시트 형태). (2026-09-28)
 * 응답자 1명 = 1행, 열 = 순번 / 시각 / 이름 / 연락처 / 성별 / 연령대 / 문항들.
 *
 * 성별·연령대(2026-10-06)는 '고객 분석' 탭과 같은 판정·라벨이다(서버 `StatisticsService`, 프론트 `AGE_BUCKETS`).
 * 수집되지 않았으면 '미수집'.
 *
 * 데이터는 부모(`SurveyStatistics`)가 한 번 받아 내려준다 — 상단 '응답자 성별·연령대' 카드와 같은 응답을 쓴다.
 * 이름·연락처는 회원 DB(`kakao_user`) 값이다. 서버(`GET /project/{id}/survey/responses`)가 채널 스코프를 걸어
 * 자기 고객사 프로젝트만 내려준다.
 *
 * 문항 열 = 현재 설문 정의 순서. 정의에 없는데 응답에만 남은 문항(제목을 바꾸거나 지운 문항)은
 * 뒤쪽에 '(이전 문항)' 으로 붙는다 — 서버 `SurveyHistoryService.toResponseSheet` 주석 참고.
 */
const FIXED_COLUMNS = [
    {title: "순번", width: 56, text: false},
    {title: "시각", width: 165, text: false},
    {title: "이름", width: 110, text: true},
    {title: "연락처", width: 150, text: true},
    {title: "성별", width: 70, text: true},
    {title: "연령대", width: 95, text: true},
];
const QUESTION_COLUMN_WIDTH = 220;
const PREVIOUS_QUESTION_SUFFIX = " (이전 문항)";

/**
 * @param sheet `{questions, rows}` — 아직 못 받았으면 null
 * @param failed 조회 실패 여부
 */
const SurveyResponseSheet = ({sheet, failed}) => {
    const grid = useMemo(() => {
        if (!sheet) return null;
        const numbers = currentQuestionNumbers(sheet.questions);
        const header = [
            ...FIXED_COLUMNS.map(column => column.title),
            // 자동 순번 '1.' 은 표시용 — 응답 키(제목)와 따로 붙는다(2026-10-12). 이전 문항은 번호 없음.
            ...sheet.questions.map(q => q.current ? `${numbers.get(q.title)} ${q.title}` : `${q.title}${PREVIOUS_QUESTION_SUFFIX}`),
        ];
        const rows = sheet.rows.map(row => [
            row.seq, row.respondedAt, row.userName, row.phoneNumber,
            genderCellText(row.gender), ageCellText(row.ageGroup),
            ...row.answers,
        ]);
        return {
            matrix: [header, ...rows],
            columnWidths: [...FIXED_COLUMNS.map(column => column.width), ...sheet.questions.map(() => QUESTION_COLUMN_WIDTH)],
            textColumns: [...FIXED_COLUMNS.map(column => column.text), ...sheet.questions.map(() => true)],
            mutedHeaders: [...FIXED_COLUMNS.map(() => false), ...sheet.questions.map(q => !q.current)],
        };
    }, [sheet]);

    const description = sheet
        ? `응답 ${sheet.rows.length.toLocaleString()}건 · 응답 시각 순`
        : "응답자별로 전체 답변을 한 줄에 표시합니다.";

    return (
        <InputGroup label="응답자별 응답" description={description}>
            {failed && <div className="text-gray-500">응답 목록을 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.</div>}
            {!failed && !grid && <div className="text-gray-500">불러오는 중…</div>}
            {grid && (
                <SurveyResponseGrid
                    matrix={grid.matrix}
                    columnWidths={grid.columnWidths}
                    textColumns={grid.textColumns}
                    mutedHeaders={grid.mutedHeaders}
                    height={680}
                    emptyMessage="아직 응답이 없습니다."
                />
            )}
        </InputGroup>
    );
};

export default SurveyResponseSheet;
